package com.solevo.dist.components

import androidx.compose.runtime.Composable
import com.solevo.libraries.entities.country.RemoteCountryService
import com.stevdza_san.library.domain.Country
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.InputGroup
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronDown
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Img

/**
 * Created by Ilija Vucetic on 24.9.25..
 * Copyright (c) 2025 Aktiia. All rights reserved.
 */
@Composable
fun PhoneNumberField(
    modifier: Modifier = Modifier,
    selectedCountry: RemoteCountryService.CountryPayload.PhoneCallingCode,
    phoneValue: String,
    onPhoneChange: (String) -> Unit,
    onCountryClick: () -> Unit,
    placeholder: String = "87 78 123",
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(Color.rgb(0xE6, 0xEA, 0xEE))
            .borderRadius(16.px)
            .padding(leftRight = 16.px, topBottom = 12.px)
            .gap(8.px), // razmak između levog i desnog dela
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Levo: flag + chevron (klikabilno)
        Row(
            modifier = Modifier
                .borderRadius(8.px)
                .cursor(Cursor.Pointer)
                .onClick { onCountryClick() }
                .gap(6.px), // razmak između zastave i chevrona
            verticalAlignment = Alignment.CenterVertically
        ) {
            Img(
                src = getFlagImageUrl(selectedCountry.isoCode.twoCharacters),
                attrs = Modifier
                    .size(24.px, 18.px)
                    .toAttrs {
                        alt("${selectedCountry.name} flag")
                    }
            )
            FaChevronDown(modifier = Modifier.size(14.px))
        }

        // Desno: prefiks + unos
        Row(
            modifier = Modifier.flex(1),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // dial code
            SpanText(
                text = "${selectedCountry.callingCode} ",
                modifier = Modifier
                    .selectionText()
            )

            // input za broj
            InputGroup(modifier = Modifier.flex(1)) {
                TextInput(
                    text = phoneValue,
                    onTextChange = { raw ->
                        val cleaned = raw.filter { it.isDigit() || it == ' ' }
                        onPhoneChange(cleaned)
                    },
                    placeholder = placeholder,
                    modifier = Modifier
                        .selectionText()
                        .color(Color.rgb(0x0B, 0x0B, 0x0B))
                        .backgroundColor(Colors.Transparent)
                        .outline(style = LineStyle.None)
                        .border(
                            style = LineStyle.None,
                            color = Colors.Transparent,
                            width = 0.px
                        )
                        .boxShadow(
                            color = Colors.Transparent,
                            blurRadius = 0.px,
                            spreadRadius = 0.px
                        )
                        .flex(1)
                )
            }
        }
    }
}

/** ISO-2 -> flag image URL using CDN. Works reliably across all platforms including Windows. */
private fun getFlagImageUrl(iso: String): String {
    val isoLower = iso.take(2).lowercase()
    if (isoLower.length != 2) return ""
    // Using flagcdn.com CDN - reliable and works on all platforms
    return "https://flagcdn.com/w40/${isoLower}.png"
}


fun Modifier.selectionText(color: Color = Color.rgb(0x0B, 0x0B, 0x0B)) = this
    .fontWeight(FontWeight.Normal)   // 400
    .fontSize(16.px)
    .lineHeight(24.px)
    .letterSpacing(0.3.px)
    .color(color)